import React, { useState } from "react";
import { createExperience, updateExperience, deleteExperience } from "../services/experienceService";
import { VscEdit, VscTrash } from "react-icons/vsc";

function ExperienceManager({ userId, experiences, onExperiencesChange }) {
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({
        team_name: "",
        position: "",
        start_date: "",
        end_date: "",
        description: "",
    });

    const resetForm = () => {
        setFormData({
            team_name: "",
            position: "",
            start_date: "",
            end_date: "",
            description: "",
        });
        setEditingId(null);
        setShowAddForm(false);
        setError("");
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!formData.team_name || !formData.start_date) return;
        // Ensure we have an authenticated userId before attempting insert
        if (!userId) {
            setError("You must be signed in to add experience.");
            return;
        }

        setSaving(true);
        setError("");

        // Convert empty end_date to null
        const dataToSubmit = {
            ...formData,
            end_date: formData.end_date || null,
        };

        const { data, error: addError } = await createExperience(userId, dataToSubmit);

        setSaving(false);

        if (addError) {
            setError(`Failed to add experience: ${addError.message}`);
        } else if (data) {
            // Add and sort by start date descending
            const updatedExperiences = [data, ...experiences].sort((a, b) =>
                new Date(b.start_date) - new Date(a.start_date)
            );
            onExperiencesChange(updatedExperiences);
            resetForm();
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!formData.team_name || !formData.start_date) return;

        setSaving(true);
        setError("");

        // Convert empty end_date to null
        const dataToSubmit = {
            ...formData,
            end_date: formData.end_date || null,
        };

        const { data, error: updateError } = await updateExperience(editingId, dataToSubmit);

        setSaving(false);

        if (updateError) {
            setError(`Failed to update experience: ${updateError.message}`);
        } else if (data) {
            // Update and resort
            const updatedExperiences = experiences.map(exp =>
                exp.id === editingId ? data : exp
            ).sort((a, b) =>
                new Date(b.start_date) - new Date(a.start_date)
            );
            onExperiencesChange(updatedExperiences);
            resetForm();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this experience?")) return;

        const { error } = await deleteExperience(id);
        if (!error) {
            onExperiencesChange(experiences.filter(exp => exp.id !== id));
        }
    };

    const startEdit = (exp) => {
        setFormData({
            team_name: exp.team_name,
            position: exp.position || "",
            start_date: exp.start_date,
            end_date: exp.end_date || "",
            description: exp.description || "",
        });
        setEditingId(exp.id);
        setShowAddForm(false);
    };

    return (
        <div className="form-section">
            <div className="section-header">
                <h3>Experience</h3>
                {!showAddForm && !editingId && (
                    <button
                        type="button"
                        className="btn-add"
                        onClick={() => setShowAddForm(true)}
                    >
                        + Add Experience
                    </button>
                )}
            </div>

            {(showAddForm || editingId) && (
                <form className="experience-form" onSubmit={editingId ? handleUpdate : handleAdd}>
                    {error && <div className="error-message">{error}</div>}
                    <div className="field-pair">
                        <label>
                            Team Name *
                            <input
                                type="text"
                                value={formData.team_name}
                                onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                                placeholder="Team name"
                                required
                            />
                        </label>
                        <label>
                            Position
                            <input
                                type="text"
                                value={formData.position}
                                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                placeholder="e.g., Midfielder"
                            />
                        </label>
                    </div>
                    <div className="field-pair">
                        <label>
                            Start Date *
                            <input
                                type="date"
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                required
                                min="1900-01-01"
                                max="9999-12-31"
                            />
                        </label>
                        <label>
                            End Date
                            <input
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                min="1900-01-01"
                                max="9999-12-31"
                            />
                            <span className="field-hint">Leave blank if current</span>
                        </label>
                    </div>
                    <label>
                        Description
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Brief description of your role and achievements..."
                            rows={2}
                        />
                    </label>
                    <div className="form-actions-inline">
                        <button type="submit" className="btn-primary-exp" disabled={saving}>
                            {saving ? "Saving..." : (editingId ? "Update" : "Add")}
                        </button>
                        <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {experiences.length > 0 && (
                <div className="experience-list">
                    {experiences.map(exp => (
                        <div key={exp.id} className="experience-item">
                            <div className="experience-item-content">
                                <h4>{exp.team_name}</h4>
                                <p className="exp-date">
                                    {new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                    {' - '}
                                    {exp.end_date ? new Date(exp.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                                </p>
                                {exp.position && <p className="exp-position">{exp.position}</p>}
                            </div>
                            <div className="experience-item-actions">
                                <button
                                    type="button"
                                    className="btn-icon-edit"
                                    onClick={() => startEdit(exp)}
                                    title="Edit"
                                >
                                    <VscEdit color="#dc2626" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleDelete(exp.id)}
                                    title="Delete"
                                >
                                    <VscTrash color="red" size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ExperienceManager;
